import type { AnalysisForm, ClaimMoveResult } from '@evogard/contract';
import {
  ANALYSIS_PHASES,
  ANALYSIS_WORK_STAGES,
  COMPENSATION_PENDENCY_KINDS,
  COMPENSATION_PHASES,
  COMPENSATION_WORK_STAGES,
  REPAIR_PHASES,
  REPAIR_WORK_STAGES,
  analysisHandoffOf,
  analysisMovesOf,
  analysisMoveDirection,
  analysisSituationLabel,
  compensationHandoffOf,
  compensationMoveNeedsForm,
  compensationMovesOf,
  compensationSituationLabel,
  isAnalysisCorrectablePhase,
  isAnalysisGeneralPhase,
  isAnalysisOutcome,
  isCompensationCorrectablePhase,
  isCompensationGeneralPhase,
  isCompensationOutcome,
  isDerivedCompensationField,
  isCatalogFieldRequired,
  isDerivedAnalysisField,
  isDerivedRepairField,
  isRepairCorrectablePhase,
  isRepairGeneralPhase,
  isRepairOutcome,
  missingAnalysisFields,
  missingCompensationFields,
  missingRepairFields,
  repairHandoffOf,
  repairMoveDirection,
  repairMovesOf,
  repairSituationLabel,
  shownAnalysisFields,
  shownCompensationFields,
  shownRepairFields,
  visibleAnalysisFields,
  visibleCompensationFields,
  visibleRepairFields,
  type AnalysisField,
  type AnalysisStage,
  type CatalogField,
  type CatalogFormContext,
  type CatalogFormState,
  type CatalogPhase,
  type ClaimDepartment,
  type CompensationField,
  type CompensationStage,
  type RepairField,
  type RepairStage,
  REPAIR_SUPERVISION_STAGES,
} from '@evogard/domain';
import { api } from '../../../lib/api.js';

/** O que a fase tem, por campo: o salvo, o rascunho por cima, e quantos arquivos. */
export function formStateOf(
  form: AnalysisForm | undefined,
  draft: Readonly<Record<string, string | null>>,
): Record<string, string | number | null> {
  const state: Record<string, string | number | null> = {
    ...(form?.values ?? {}),
    /* A observação do Reparo conta os comentários; um escrito agora conta como um a mais. */
    ...(form?.counts ?? {}),
    ...draft,
  };
  for (const [key, n] of Object.entries(form?.counts ?? {})) {
    if (typeof draft[key] === 'string' && draft[key]!.trim() !== '') state[key] = n + 1;
    else state[key] = n;
  }
  for (const [key, list] of Object.entries(form?.files ?? {})) state[key] = list.length;
  return state;
}

/** O retorno de uma chamada do contrato: o status e o corpo. */
type Reply<T> = { status: number; body: T | unknown };

/**
 * Um departamento com mesa, como a tela o vê — a Análise e o Reparo
 * (07/10/2026), e a Indenização (09/10/2026). O par de `apps/api/src/modules/claims/claims.desk.ts`: a mesa,
 * o formulário, o quadro e os registros recebem um destes e não perguntam
 * qual departamento é.
 */
export interface Desk {
  department: ClaimDepartment;
  /** "Análise", "Reparo", "Indenização". */
  name: string;
  /** "a Análise", "o Reparo". */
  the: string;
  /** "na Análise", "no Reparo". */
  in: string;
  /** "da Análise", "do Reparo". */
  of: string;
  /** O recurso que move e grava, e o que vê. */
  resource: 'claims.analyses' | 'claims.repairs' | 'claims.compensations';
  /** As etapas de trabalho, na ordem — as colunas do quadro e a trilha. */
  workStages: readonly string[];
  phaseOf(code: string): CatalogPhase | undefined;
  movesOf(
    phase: string,
    hold: string | null,
    context: CatalogFormContext,
    /** O estado da fase — a Análise o pergunta: com o veículo em base, Negado sai. */
    state?: CatalogFormState,
  ): { forward: string[]; handoffs: string[]; outcomes: string[]; back: string[] };
  handoffOf(code: string): { department: ClaimDepartment; phase: string } | undefined;
  /**
   * Ir de `from` para `to` pede os obrigatórios de `from`? Avançar pede e
   * voltar não — e o desvio também não: o Acordo da Indenização sai da
   * Documentação sem o checklist (I5, 09/10/2026). A mesma pergunta que a API
   * faz antes de mover.
   */
  needsForm(from: string, to: string): boolean;
  isOutcome(code: string): boolean;
  isGeneralPhase(code: string): boolean;
  isCorrectable(phase: string, current: string, visited: readonly string[]): boolean;
  visibleFields(phase: string): CatalogField[];
  shownFields(
    phase: string,
    state: CatalogFormState,
    context: CatalogFormContext,
  ): CatalogField[];
  missingFields(
    phase: string,
    state: CatalogFormState,
    context: CatalogFormContext,
  ): CatalogField[];
  isDerived(field: CatalogField): boolean;
  situationLabel(code: string): string;
  /** "Analista responsável", "Responsável do reparo". */
  assigneeLabel: string;
  /** "Sem analista", "Sem responsável". */
  noAssignee: string;
  /**
   * Os tipos da pendência, onde o departamento os tem — a Indenização (I2,
   * 09/10/2026). Sem eles, a pendência não tem tipo.
   */
  pendencyKinds?: Readonly<Record<string, string>>;
  /**
   * As etapas do supervisor, e a permissão dele — a Aprovação Supervisor do
   * Reparo (a Supervisão, que vinha antes, saiu em 03/11/2026).
   */
  supervision?: {
    phases: readonly string[];
    resource: 'claims.repairs';
    action: 'supervisionar';
  };
  /** As rotas da mesa deste departamento. */
  api: DeskApi;
}

export interface DeskApi {
  form(id: number, phase: string): Promise<Reply<AnalysisForm>>;
  save(
    id: number,
    phase: string,
    values: Record<string, string | null>,
  ): Promise<Reply<AnalysisForm>>;
  attach(
    id: number,
    phase: string,
    field: string,
    body: { fileName: string; mimeType: string; content: string },
  ): Promise<Reply<AnalysisForm>>;
  archive(
    id: number,
    phase: string,
    field: string,
    documentId: number,
  ): Promise<Reply<AnalysisForm>>;
  move(
    id: number,
    body: { fromPhase: string; toPhase: string },
  ): Promise<Reply<ClaimMoveResult>>;
  undo(id: number, passageId: number): Promise<Reply<ClaimMoveResult>>;
  contact(id: number, body: ContactBody): Promise<Reply<ClaimMoveResult>>;
  hold(
    id: number,
    body: {
      fromPhase: string;
      type: 'pendencia' | 'pre_desistencia' | 'negado_com_posse' | 'juridico';
      text?: string;
      pendencyKind?: string;
    },
  ): Promise<Reply<ClaimMoveResult>>;
  resume(
    id: number,
    body: { fromPhase: string; note?: string },
  ): Promise<Reply<ClaimMoveResult>>;
  setAssignee(id: number, userId: string | null): Promise<Reply<unknown>>;
  assigneeOptions(): Promise<Reply<{ id: string; name: string }[]>>;
}

/** A tentativa de contato, como a mesa a manda. */
export interface ContactBody {
  fromPhase: string;
  channel: 'ligacao' | 'whatsapp' | 'email';
  answered: boolean;
  phone?: string;
  email?: string;
  protocol?: string;
  notes?: string;
  /** A oficina do reparo — só no Reparo. */
  companyId?: number;
  /** A evidência — o print do chat ou da ligação —, obrigatória desde 21/10/2026. */
  attachment: { fileName: string; mimeType: string; content: string };
}

const analysisPhase = (code: string) =>
  (ANALYSIS_PHASES as Record<string, CatalogPhase | undefined>)[code];
const repairPhase = (code: string) =>
  (REPAIR_PHASES as Record<string, CatalogPhase | undefined>)[code];
const compensationPhase = (code: string) =>
  (COMPENSATION_PHASES as Record<string, CatalogPhase | undefined>)[code];

export const ANALYSIS_DESK: Desk = {
  department: 'analise',
  name: 'Análise',
  the: 'a Análise',
  in: 'na Análise',
  of: 'da Análise',
  resource: 'claims.analyses',
  workStages: ANALYSIS_WORK_STAGES,
  phaseOf: analysisPhase,
  movesOf: (phase, hold, _ctx, state) =>
    analysisMovesOf(phase as AnalysisStage, hold, state),
  handoffOf: analysisHandoffOf,
  needsForm: (from, to) =>
    analysisMoveDirection(from as AnalysisStage, to as AnalysisStage) === 'avancar',
  isOutcome: isAnalysisOutcome,
  isGeneralPhase: isAnalysisGeneralPhase,
  isCorrectable: (phase, current, visited) =>
    isAnalysisCorrectablePhase(phase as AnalysisStage, current as AnalysisStage, visited),
  visibleFields: (phase) => visibleAnalysisFields(phase as AnalysisStage),
  shownFields: (phase, state, ctx) =>
    shownAnalysisFields(phase as AnalysisStage, state, ctx),
  missingFields: (phase, state, ctx) =>
    missingAnalysisFields(phase as AnalysisStage, state, ctx),
  isDerived: (field) => isDerivedAnalysisField(field as AnalysisField),
  situationLabel: analysisSituationLabel,
  assigneeLabel: 'Analista responsável',
  noAssignee: 'Sem analista',
  api: {
    form: (id, phase) => api.claims.getAnalysisForm({ params: { id, phase } }),
    save: (id, phase, values) =>
      api.claims.saveAnalysisForm({ params: { id, phase }, body: { values } }),
    attach: (id, phase, field, body) =>
      api.claims.attachAnalysisFile({ params: { id, phase, field }, body }),
    archive: (id, phase, field, documentId) =>
      api.claims.archiveAnalysisFile({
        params: { id, phase, field, documentId },
        body: {},
      }),
    move: (id, body) => api.claims.moveClaim({ params: { id }, body }),
    undo: (id, passageId) =>
      api.claims.undoClaimMove({ params: { id }, body: { passageId } }),
    contact: (id, body) => api.claims.registerClaimContact({ params: { id }, body }),
    hold: (id, body) => api.claims.holdClaim({ params: { id }, body }),
    resume: (id, body) => api.claims.resumeClaim({ params: { id }, body }),
    setAssignee: (id, userId) =>
      api.claims.setAnalysisAssignee({ params: { id }, body: { userId } }),
    assigneeOptions: () => api.claims.listAssigneeOptions(),
  },
};

export const REPAIR_DESK: Desk = {
  department: 'reparo',
  name: 'Reparo',
  the: 'o Reparo',
  in: 'no Reparo',
  of: 'do Reparo',
  resource: 'claims.repairs',
  workStages: REPAIR_WORK_STAGES,
  phaseOf: repairPhase,
  movesOf: (phase, hold, ctx) => repairMovesOf(phase as RepairStage, hold, ctx),
  handoffOf: repairHandoffOf,
  needsForm: (from, to) =>
    repairMoveDirection(from as RepairStage, to as RepairStage) === 'avancar',
  isOutcome: isRepairOutcome,
  isGeneralPhase: isRepairGeneralPhase,
  isCorrectable: (phase, current, visited) =>
    isRepairCorrectablePhase(phase as RepairStage, current as RepairStage, visited),
  visibleFields: (phase) => visibleRepairFields(phase as RepairStage),
  shownFields: (phase, state, ctx) => shownRepairFields(phase as RepairStage, state, ctx),
  missingFields: (phase, state, ctx) =>
    missingRepairFields(phase as RepairStage, state, ctx),
  isDerived: (field) => isDerivedRepairField(field as RepairField),
  situationLabel: repairSituationLabel,
  assigneeLabel: 'Responsável do reparo',
  noAssignee: 'Sem responsável',
  supervision: {
    phases: REPAIR_SUPERVISION_STAGES,
    resource: 'claims.repairs',
    action: 'supervisionar',
  },
  api: {
    form: (id, phase) => api.claims.getRepairForm({ params: { id, phase } }),
    save: (id, phase, values) =>
      api.claims.saveRepairForm({ params: { id, phase }, body: { values } }),
    attach: (id, phase, field, body) =>
      api.claims.attachRepairFile({ params: { id, phase, field }, body }),
    archive: (id, phase, field, documentId) =>
      api.claims.archiveRepairFile({
        params: { id, phase, field, documentId },
        body: {},
      }),
    move: (id, body) => api.claims.moveRepairClaim({ params: { id }, body }),
    undo: (id, passageId) =>
      api.claims.undoRepairClaimMove({ params: { id }, body: { passageId } }),
    contact: (id, body) => api.claims.registerRepairContact({ params: { id }, body }),
    hold: (id, body) => api.claims.holdRepairClaim({ params: { id }, body }),
    resume: (id, body) => api.claims.resumeRepairClaim({ params: { id }, body }),
    setAssignee: (id, userId) =>
      api.claims.setRepairAssignee({ params: { id }, body: { userId } }),
    assigneeOptions: () => api.claims.listRepairAssigneeOptions(),
  },
};

/**
 * A Indenização (09/10/2026), sobre o catálogo do de-para aprovado no mesmo
 * dia (`claims-compensation.ts`). Sem supervisão; a pendência tem tipo (I2); o
 * Acordo é envio de volta ao Reparo, e desvio (I5).
 */
export const COMPENSATION_DESK: Desk = {
  department: 'indenizacao',
  name: 'Indenização',
  the: 'a Indenização',
  in: 'na Indenização',
  of: 'da Indenização',
  resource: 'claims.compensations',
  workStages: COMPENSATION_WORK_STAGES,
  phaseOf: compensationPhase,
  movesOf: (phase, hold, ctx) =>
    compensationMovesOf(phase as CompensationStage, hold, ctx),
  handoffOf: compensationHandoffOf,
  needsForm: (from, to) =>
    compensationMoveNeedsForm(from as CompensationStage, to as CompensationStage),
  isOutcome: isCompensationOutcome,
  isGeneralPhase: isCompensationGeneralPhase,
  isCorrectable: (phase, current, visited) =>
    isCompensationCorrectablePhase(
      phase as CompensationStage,
      current as CompensationStage,
      visited,
    ),
  visibleFields: (phase) => visibleCompensationFields(phase as CompensationStage),
  shownFields: (phase, state, ctx) =>
    shownCompensationFields(phase as CompensationStage, state, ctx),
  missingFields: (phase, state, ctx) =>
    missingCompensationFields(phase as CompensationStage, state, ctx),
  isDerived: (field) => isDerivedCompensationField(field as CompensationField),
  situationLabel: compensationSituationLabel,
  assigneeLabel: 'Analista de indenização',
  noAssignee: 'Sem analista',
  pendencyKinds: COMPENSATION_PENDENCY_KINDS,
  api: {
    form: (id, phase) =>
      api.claimCompensations.getCompensationForm({ params: { id, phase } }),
    save: (id, phase, values) =>
      api.claimCompensations.saveCompensationForm({
        params: { id, phase },
        body: { values },
      }),
    attach: (id, phase, field, body) =>
      api.claimCompensations.attachCompensationFile({
        params: { id, phase, field },
        body,
      }),
    archive: (id, phase, field, documentId) =>
      api.claimCompensations.archiveCompensationFile({
        params: { id, phase, field, documentId },
        body: {},
      }),
    move: (id, body) =>
      api.claimCompensations.moveCompensationClaim({ params: { id }, body }),
    undo: (id, passageId) =>
      api.claimCompensations.undoCompensationClaimMove({
        params: { id },
        body: { passageId },
      }),
    contact: (id, body) =>
      api.claimCompensations.registerCompensationContact({ params: { id }, body }),
    hold: (id, body) =>
      api.claimCompensations.holdCompensationClaim({ params: { id }, body }),
    resume: (id, body) =>
      api.claimCompensations.resumeCompensationClaim({ params: { id }, body }),
    setAssignee: (id, userId) =>
      api.claimCompensations.setCompensationAssignee({
        params: { id },
        body: { userId },
      }),
    assigneeOptions: () => api.claimCompensations.listCompensationAssigneeOptions(),
  },
};

/** A mesa do departamento onde o sinistro está — `null` fora dos que têm mesa. */
export function deskOf(department: ClaimDepartment | null | undefined): Desk | null {
  if (department === 'analise') return ANALYSIS_DESK;
  if (department === 'reparo') return REPAIR_DESK;
  if (department === 'indenizacao') return COMPENSATION_DESK;
  return null;
}

/** O campo é exigido agora? A regra é a mesma nos dois catálogos. */
export const isFieldRequired = isCatalogFieldRequired;

/**
 * O nome do destino: o envio leva o nome do departamento, e não o da antiga
 * fase do pipe ("Aprovado para Reparo" é o Reparo, "Indenizados" é a
 * Indenização) — 07/10/2026.
 */
export function destinationLabelOf(
  desk: Desk,
  code: string,
  departmentLabel: Record<ClaimDepartment, string>,
): string {
  const handoff = desk.handoffOf(code);
  return handoff
    ? departmentLabel[handoff.department]
    : (desk.phaseOf(code)?.label ?? code);
}
